import { useState, useEffect, useCallback } from "react";
import { crApi } from "../lib/crScope";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  Search, Download, Eye, Trash2, FileSpreadsheet, FileText,
  CheckCircle2, XCircle, Calendar, User, Building2, RefreshCw,
  Edit, Save, MinusCircle, Ticket, History, Clock, Sparkles, FileUp, MessageCircleQuestion, ChevronUp, ChevronDown, ShieldCheck
} from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";

// Paleta de degradados para las tarjetas por ejecutivo (estilo KPI de Renovaciones)
const EJECUTIVO_CARD_PALETTE = [
  { grad: "from-blue-500 to-blue-600", soft: "text-blue-100", softer: "text-blue-200", border: "border-blue-400/30", bar: "bg-blue-800" },
  { grad: "from-emerald-500 to-emerald-600", soft: "text-emerald-100", softer: "text-emerald-200", border: "border-emerald-400/30", bar: "bg-emerald-800" },
  { grad: "from-indigo-500 to-indigo-600", soft: "text-indigo-100", softer: "text-indigo-200", border: "border-indigo-400/30", bar: "bg-indigo-800" },
  { grad: "from-rose-500 to-rose-600", soft: "text-rose-100", softer: "text-rose-200", border: "border-rose-400/30", bar: "bg-rose-800" },
  { grad: "from-purple-500 to-purple-600", soft: "text-purple-100", softer: "text-purple-200", border: "border-purple-400/30", bar: "bg-purple-800" },
  { grad: "from-amber-500 to-amber-600", soft: "text-amber-100", softer: "text-amber-200", border: "border-amber-400/30", bar: "bg-amber-800" },
  { grad: "from-cyan-500 to-cyan-600", soft: "text-cyan-100", softer: "text-cyan-200", border: "border-cyan-400/30", bar: "bg-cyan-800" },
  { grad: "from-teal-500 to-teal-600", soft: "text-teal-100", softer: "text-teal-200", border: "border-teal-400/30", bar: "bg-teal-800" },
];

export default function HistorialChecklist({ API_URL, getAuthHeaders, canEdit, currentUser, origen }) {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [userRole, setUserRole] = useState("usuario");
  
  // Determine if user is admin (can delete)
  const isAdmin = currentUser?.role?.toLowerCase() === "admin" || 
                  currentUser?.role?.toLowerCase() === "administrador" ||
                  currentUser?.is_admin;
  // Admin or Supervisor can see the duration ("Tiempo realización")
  const isAdminOrSupervisor = isAdmin
    || (currentUser?.role || "").toLowerCase().includes("supervisor")
    || userRole === "admin" || userRole === "supervisor";
  
  // Filters
  const [searchCliente, setSearchCliente] = useState("");
  const [debouncedCliente, setDebouncedCliente] = useState("");
  const [filterRamo, setFilterRamo] = useState("");
  const [filterEjecutivo, setFilterEjecutivo] = useState("");
  const [filterFechaDesde, setFilterFechaDesde] = useState("");
  const [filterFechaHasta, setFilterFechaHasta] = useState("");
  const [filterVenceEn, setFilterVenceEn] = useState("");
  const [venceEnOptions, setVenceEnOptions] = useState([]);
  const [filterRevisado, setFilterRevisado] = useState("");
  const [togglingRevisado, setTogglingRevisado] = useState(false);
  
  // View dialog
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  
  // Edit dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState(null);
  const [editItemsRespuesta, setEditItemsRespuesta] = useState([]);
  const [editObservaciones, setEditObservaciones] = useState("");
  const [editSupervisadoPor, setEditSupervisadoPor] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Edit tipo cobertura
  const [editingTipoCobertura, setEditingTipoCobertura] = useState(false);
  const [newTipoCobertura, setNewTipoCobertura] = useState("");
  
  // Bulk update tipo cobertura dialog
  const [showBulkUpdateDialog, setShowBulkUpdateDialog] = useState(false);
  const [checklistsSinTipo, setChecklistsSinTipo] = useState([]);
  const [bulkTipoCobertura, setBulkTipoCobertura] = useState("");
  const [loadingBulk, setLoadingBulk] = useState(false);
  
  // Available ramos for filter
  const [ramosDisponibles, setRamosDisponibles] = useState([]);

  // Tarjetas de estadísticas por ejecutivo (solo admin/supervisor)
  const [ejecutivoStats, setEjecutivoStats] = useState([]);
  const [statsPrivileged, setStatsPrivileged] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(() => {
    const saved = localStorage.getItem("historial_checklist_cards_visible");
    return saved === null ? true : saved === "true";
  });

  const toggleCardsVisible = () => {
    setCardsVisible((prev) => {
      const next = !prev;
      localStorage.setItem("historial_checklist_cards_visible", String(next));
      return next;
    });
  };

  const fetchEjecutivoStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterVenceEn) params.set("vence_en", filterVenceEn);
      if (origen) params.set("origen", origen);
      const qs = params.toString();
      const url = `${API_URL}${crApi()}/control-renovaciones/checklist-stats-ejecutivos${qs ? "?" + qs : ""}`;
      const res = await fetch(url, getAuthHeaders());
      const data = await res.json();
      setStatsPrivileged(!!data.is_privileged);
      setEjecutivoStats(data.cards || []);
    } catch (error) {
      console.error("Error fetching ejecutivo stats:", error);
    }
  }, [API_URL, getAuthHeaders, filterVenceEn, origen]);

  const fetchHistorial = useCallback(async () => {
    try {
      let url = `${API_URL}${crApi()}/checklist-renovaciones/historial?limit=100`;
      if (debouncedCliente) url += `&cliente=${encodeURIComponent(debouncedCliente)}`;
      if (filterRamo) url += `&ramo=${encodeURIComponent(filterRamo)}`;
      if (filterEjecutivo) url += `&realizado_por=${encodeURIComponent(filterEjecutivo)}`;
      if (filterFechaDesde) url += `&fecha_desde=${filterFechaDesde}`;
      if (filterFechaHasta) url += `&fecha_hasta=${filterFechaHasta}`;
      if (filterVenceEn) url += `&vence_en=${encodeURIComponent(filterVenceEn)}`;
      if (filterRevisado) url += `&revisado=${filterRevisado}`;
      if (origen) url += `&origen=${origen}`;
      
      const res = await fetch(url, getAuthHeaders());
      const data = await res.json();
      setChecklists(data.items || []);
      setTotal(data.total || 0);
      setUserRole(data.user_role || "usuario");
      if (Array.isArray(data.vence_en_options)) setVenceEnOptions(data.vence_en_options);
      
      // Extract unique ramos
      const ramos = [...new Set((data.items || []).map(c => c.ramo).filter(Boolean))];
      setRamosDisponibles(ramos);
    } catch (error) {
      console.error("Error fetching historial:", error);
      toast.error("Error al cargar historial");
    }
  }, [API_URL, getAuthHeaders, debouncedCliente, filterRamo, filterEjecutivo, filterFechaDesde, filterFechaHasta, filterVenceEn, filterRevisado]);

  // Debounce del buscador de cliente: evita disparar un fetch (y el spinner de
  // carga) en CADA tecla, lo que provocaba que el campo "se refrescara" y no se
  // pudiera escribir el nombre completo de corrido. El fetch ahora ocurre 400ms
  // después de dejar de teclear.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedCliente(searchCliente), 400);
    return () => clearTimeout(t);
  }, [searchCliente]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchHistorial();
      setLoading(false);
    };
    loadData();
  }, [fetchHistorial]);

  useEffect(() => {
    fetchEjecutivoStats();
  }, [fetchEjecutivoStats]);

  const handleViewChecklist = async (checklist) => {
    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/historial/${checklist.id}`, getAuthHeaders());
      const data = await res.json();
      setSelectedChecklist(data);
      setEditingTipoCobertura(false);
      setNewTipoCobertura(data.tipo_cobertura_seleccionado || "");
      setShowViewDialog(true);
    } catch (error) {
      toast.error("Error al cargar checklist");
    }
  };

  const handleToggleRevisado = async (checklistId, value) => {
    setTogglingRevisado(true);
    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/historial/${checklistId}/revisado`, {
        method: "PATCH",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ revisado: value })
      });
      if (res.ok) {
        const updated = await res.json();
        toast.success(value ? "Checklist marcado como Revisado" : "Marca de Revisado quitada");
        // Sync both dialogs (Ver / Editar) and the table row
        setSelectedChecklist(prev => prev && prev.id === checklistId
          ? { ...prev, revisado: updated.revisado, revisado_por: updated.revisado_por, revisado_at: updated.revisado_at, trazabilidad: updated.trazabilidad }
          : prev);
        setEditingChecklist(prev => prev && prev.id === checklistId
          ? { ...prev, revisado: updated.revisado, revisado_por: updated.revisado_por, revisado_at: updated.revisado_at }
          : prev);
        setChecklists(prev => prev.map(c => c.id === checklistId
          ? { ...c, revisado: updated.revisado, revisado_por: updated.revisado_por, revisado_at: updated.revisado_at }
          : c));
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No tiene permiso para revisar este checklist");
      }
    } catch (error) {
      toast.error("Error al actualizar el estado de revisión");
    } finally {
      setTogglingRevisado(false);
    }
  };

  const handleSaveTipoCobertura = async () => {
    if (!selectedChecklist || !newTipoCobertura.trim()) {
      toast.error("Ingrese un tipo de cobertura");
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/historial/${selectedChecklist.id}/tipo-cobertura`, {
        method: "PATCH",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ tipo_cobertura: newTipoCobertura.trim() })
      });
      
      if (res.ok) {
        toast.success("Tipo de cobertura actualizado");
        // Update local state
        setSelectedChecklist(prev => ({ ...prev, tipo_cobertura_seleccionado: newTipoCobertura.trim() }));
        setEditingTipoCobertura(false);
        // Refresh the list
        fetchHistorial();
      } else {
        const errorData = await res.json();
        toast.error(errorData.detail || "Error al actualizar");
      }
    } catch (error) {
      toast.error("Error al actualizar tipo de cobertura");
    }
  };

  // Funciones para actualización masiva de tipo de cobertura
  const fetchChecklistsSinTipo = async () => {
    try {
      setLoadingBulk(true);
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/historial/sin-tipo-cobertura/listar`, getAuthHeaders());
      if (res.ok) {
        const data = await res.json();
        setChecklistsSinTipo(data.checklists || []);
        setShowBulkUpdateDialog(true);
      } else {
        toast.error("Error al buscar checklists sin tipo de cobertura");
      }
    } catch (error) {
      toast.error("Error al buscar checklists");
    } finally {
      setLoadingBulk(false);
    }
  };

  const handleBulkUpdateTipoCobertura = async () => {
    if (!bulkTipoCobertura) {
      toast.error("Seleccione un tipo de cobertura");
      return;
    }
    
    if (checklistsSinTipo.length === 0) {
      toast.error("No hay checklists para actualizar");
      return;
    }
    
    try {
      setLoadingBulk(true);
      const ids = checklistsSinTipo.map(c => c.id);
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/historial/actualizar-tipo-cobertura-masivo`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          checklist_ids: ids,
          tipo_cobertura: bulkTipoCobertura
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || "Checklists actualizados");
        setShowBulkUpdateDialog(false);
        setChecklistsSinTipo([]);
        setBulkTipoCobertura("");
        fetchHistorial(); // Refresh the list
      } else {
        const errorData = await res.json();
        toast.error(errorData.detail || "Error al actualizar");
      }
    } catch (error) {
      toast.error("Error al actualizar checklists");
    } finally {
      setLoadingBulk(false);
    }
  };

  const handleExportExcel = async (checklistId) => {
    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/historial/${checklistId}/export/excel`, getAuthHeaders());
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `checklist_${checklistId}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch (error) {
      toast.error("Error al exportar Excel");
    }
  };

  const handleExportPDF = async (checklistId) => {
    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/historial/${checklistId}/export/pdf`, getAuthHeaders());
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `checklist_${checklistId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (error) {
      toast.error("Error al exportar PDF");
    }
  };

  const handleExportTrazabilidadPDF = async (checklistId) => {
    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/historial/${checklistId}/trazabilidad/pdf`, getAuthHeaders());
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No tiene permiso para imprimir la trazabilidad");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `trazabilidad_checklist_${checklistId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Trazabilidad descargada");
    } catch (error) {
      toast.error("Error al imprimir la trazabilidad");
    }
  };

  const [exportingReport, setExportingReport] = useState(false);
  const handleExportReportExcel = async () => {
    setExportingReport(true);
    try {
      const params = new URLSearchParams();
      if (searchCliente) params.append("cliente", searchCliente);
      if (filterRamo) params.append("ramo", filterRamo);
      if (filterEjecutivo) params.append("realizado_por", filterEjecutivo);
      if (filterFechaDesde) params.append("fecha_desde", filterFechaDesde);
      if (filterFechaHasta) params.append("fecha_hasta", filterFechaHasta);
      if (origen) params.append("origen", origen);
      const qs = params.toString();
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/historial/export/excel${qs ? "?" + qs : ""}`, getAuthHeaders());
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Error al generar reporte");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `reporte_historial_checklists_${today}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Reporte Excel descargado");
    } catch (error) {
      toast.error(error.message || "Error al exportar reporte");
    } finally {
      setExportingReport(false);
    }
  };

  const handleDeleteChecklist = async (checklist) => {
    if (!window.confirm(`¿Eliminar el checklist de "${checklist.cliente}"?`)) return;

    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/historial/${checklist.id}`, {
        method: "DELETE",
        ...getAuthHeaders()
      });

      if (res.ok) {
        toast.success("Checklist eliminado");
        fetchHistorial();
      } else {
        toast.error("Error al eliminar");
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // === EDIT FUNCTIONS ===
  const handleEditChecklist = async (checklist) => {
    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/historial/${checklist.id}`, getAuthHeaders());
      const data = await res.json();
      setEditingChecklist(data);
      
      // Initialize edit state from checklist data
      setEditItemsRespuesta(data.items?.map(item => ({
        ...item,
        estado: item.no_aplica ? "no_aplica" : (item.tiene_cobertura ? "si" : "pendiente")
      })) || []);
      setEditObservaciones(data.observaciones_generales || "");
      setEditSupervisadoPor(data.supervisado_por || "");
      
      setShowEditDialog(true);
    } catch (error) {
      toast.error("Error al cargar checklist para edición");
    }
  };

  const handleEditItemEstadoChange = (itemId, nuevoEstado) => {
    setEditItemsRespuesta(prev => prev.map(item => 
      item.item_id === itemId 
        ? { ...item, estado: nuevoEstado }
        : item
    ));
  };

  const handleEditComentarioEjecutivo = (itemId, value) => {
    setEditItemsRespuesta(prev => prev.map(item => 
      item.item_id === itemId 
        ? { ...item, comentario_ejecutivo: value }
        : item
    ));
  };

  const handleEditComentarioSupervisor = (itemId, value) => {
    setEditItemsRespuesta(prev => prev.map(item => 
      item.item_id === itemId 
        ? { ...item, comentario_supervisor: value }
        : item
    ));
  };

  const handleSaveEdit = async () => {
    if (!editingChecklist) return;

    setSaving(true);
    try {
      // Convert estado to tiene_cobertura for backend compatibility
      const itemsForBackend = editItemsRespuesta.map(item => ({
        item_id: item.item_id,
        texto: item.texto,
        categoria: item.categoria,
        tipo_cobertura: item.tipo_cobertura,
        tiene_cobertura: item.estado === "si",
        no_aplica: item.estado === "no_aplica",
        comentario_ejecutivo: item.comentario_ejecutivo || "",
        comentario_supervisor: item.comentario_supervisor || "",
        cotizacion_estado: item.cotizacion_estado || null,
        diferencia: item.diferencia || null,
        monto_asegurado: item.monto_asegurado || null,
        ajuste_solicitado: item.ajuste_solicitado || null
      }));

      const payload = {
        items: itemsForBackend,
        observaciones_generales: editObservaciones || null,
        supervisado_por: editSupervisadoPor || null
      };

      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/historial/${editingChecklist.id}`, {
        method: "PUT",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(`Checklist actualizado - ${result.porcentaje_cobertura}% de cobertura`);
        setShowEditDialog(false);
        setEditingChecklist(null);
        fetchHistorial();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Error al actualizar checklist");
      }
    } catch (error) {
      console.error("Error updating checklist:", error);
      toast.error("Error al actualizar checklist");
    } finally {
      setSaving(false);
    }
  };

  const closeEditDialog = () => {
    setShowEditDialog(false);
    setEditingChecklist(null);
    setEditItemsRespuesta([]);
    setEditObservaciones("");
    setEditSupervisadoPor("");
  };

  // Group edit items by category
  const groupedEditItems = editItemsRespuesta.reduce((acc, item) => {
    const cat = item.categoria || "General";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  // Edit stats - exclude N/A from calculation
  const editItemsAplicables = editItemsRespuesta.filter(i => i.estado !== "no_aplica");
  const editTotalItems = editItemsAplicables.length;
  const editItemsConCobertura = editItemsAplicables.filter(i => i.estado === "si").length;
  const editItemsNoAplica = editItemsRespuesta.filter(i => i.estado === "no_aplica").length;
  const editPorcentaje = editTotalItems > 0 ? Math.round((editItemsConCobertura / editTotalItems) * 100) : 0;

  const clearFilters = () => {
    setSearchCliente("");
    setFilterRamo("");
    setFilterEjecutivo("");
    setFilterFechaDesde("");
    setFilterFechaHasta("");
    setFilterRevisado("");
  };

  const getColorByPercentage = (porcentaje) => {
    if (porcentaje >= 80) return "bg-green-100 text-green-800";
    if (porcentaje >= 50) return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Historial de Checklists</h2>
          <p className="text-sm text-slate-500">Checklists completados ({total} registros)</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            onClick={handleExportReportExcel}
            disabled={exportingReport}
            className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
            data-testid="export-historial-report-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {exportingReport ? "Generando…" : "Exportar Excel (Reporte)"}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchChecklistsSinTipo} disabled={loadingBulk}>
            <Edit className="w-4 h-4 mr-2" />
            Actualizar Tipos de Cobertura
          </Button>
          <Button variant="outline" size="sm" onClick={fetchHistorial}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Filtro por mes de vencimiento (Vence en) — afecta tabla y tarjetas */}
      <div className="flex flex-wrap items-center gap-2" data-testid="historial-vence-filter-bar">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Vence en:</span>
        </div>
        <Select
          value={filterVenceEn || "ALL"}
          onValueChange={(v) => setFilterVenceEn(v === "ALL" ? "" : v)}
        >
          <SelectTrigger className="w-[220px]" data-testid="filter-vence-en-select">
            <SelectValue placeholder="Todos los meses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos los meses</SelectItem>
            {venceEnOptions.map((m) => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filterVenceEn && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilterVenceEn("")}
            data-testid="clear-vence-en-filter-btn"
          >
            <XCircle className="w-4 h-4 mr-1" /> Quitar filtro
          </Button>
        )}
        {isAdminOrSupervisor && (
          <div className="flex items-center gap-2 ml-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Revisión:</span>
            <Select
              value={filterRevisado || "ALL"}
              onValueChange={(v) => setFilterRevisado(v === "ALL" ? "" : v)}
            >
              <SelectTrigger className="w-[180px]" data-testid="filter-revisado-select">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos</SelectItem>
                <SelectItem value="true">Solo revisados</SelectItem>
                <SelectItem value="false">Sin revisar</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Tarjetas por ejecutivo (solo admin/supervisor) */}
      {statsPrivileged && ejecutivoStats.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Resumen por Ejecutivo</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleCardsVisible}
              data-testid="toggle-ejecutivo-cards-btn"
            >
              {cardsVisible ? (
                <><ChevronUp className="w-4 h-4 mr-2" /> Ocultar tarjetas</>
              ) : (
                <><ChevronDown className="w-4 h-4 mr-2" /> Mostrar tarjetas</>
              )}
            </Button>
          </div>
          {cardsVisible && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="ejecutivo-stats-cards">
              {ejecutivoStats.map((c, idx) => {
            const p = EJECUTIVO_CARD_PALETTE[idx % EJECUTIVO_CARD_PALETTE.length];
            const pctRenovadas = c.renovadas_total > 0
              ? Math.round((c.renovadas_con_checklist / c.renovadas_total) * 100)
              : 0;
            return (
              <Card
                key={c.ejecutivo}
                onClick={() => setFilterEjecutivo((prev) => (prev === c.ejecutivo ? "" : c.ejecutivo))}
                className={`bg-gradient-to-br ${p.grad} text-white border-0 shadow-md cursor-pointer transition-all hover:shadow-xl hover:-translate-y-0.5 ${filterEjecutivo === c.ejecutivo ? "ring-4 ring-white/70 -translate-y-0.5 shadow-xl" : ""}`}
                title={filterEjecutivo === c.ejecutivo ? "Click para quitar el filtro" : `Click para filtrar por ${c.ejecutivo}`}
                data-testid={`ejecutivo-card-${c.ejecutivo}`}
              >
                <CardContent className="p-4">
                  {/* Encabezado: ejecutivo + total renovadas + icono */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0">
                      <p className={`${p.soft} text-sm truncate`} title={c.ejecutivo}>{c.ejecutivo}</p>
                      <p className="text-3xl font-bold leading-tight">{c.renovadas_total}</p>
                      <p className={`${p.softer} text-xs`}>{origen === "cotizaciones" ? "Riesgos Aceptados" : "Pólizas Renovadas"}</p>
                    </div>
                    <div className="flex flex-col items-end shrink-0">
                      <div className="bg-white/20 rounded-lg p-2">
                        <User className="w-6 h-6 text-white" />
                      </div>
                      <span className="mt-2 text-xs font-semibold bg-white/20 px-2 py-0.5 rounded-full">
                        {pctRenovadas}%
                      </span>
                    </div>
                  </div>

                  {/* Progreso de checklist en renovadas */}
                  <div className={`border-t ${p.border} pt-3`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`${p.softer} text-xs`}>{origen === "cotizaciones" ? "Checklist de aceptados" : "Checklist de renovadas"}</span>
                      <span className="text-sm font-bold">{c.renovadas_con_checklist}/{c.renovadas_total}</span>
                    </div>
                    <Progress value={pctRenovadas} className={`h-2 ${p.bar}`} />
                    <div className="flex justify-between mt-2 text-xs">
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Con: <strong>{c.renovadas_con_checklist}</strong>
                      </span>
                      <span className="flex items-center gap-1">
                        <XCircle className="w-3.5 h-3.5" /> Sin: <strong>{c.renovadas_sin_checklist}</strong>
                      </span>
                    </div>
                  </div>

                  {/* Otros estatus */}
                  <div className={`border-t ${p.border} pt-3 mt-3`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`${p.softer} text-xs`}>Otros estatus</span>
                      <span className="text-sm font-bold">{c.otras_total}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Con: <strong>{c.otras_con_checklist}</strong>
                      </span>
                      <span className="flex items-center gap-1">
                        <XCircle className="w-3.5 h-3.5" /> Sin: <strong>{c.otras_sin_checklist}</strong>
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-slate-500">Buscar Cliente</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Nombre del cliente..."
                  value={searchCliente}
                  onChange={(e) => setSearchCliente(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-40">
              <Label className="text-xs text-slate-500">Ramo</Label>
              <Select value={filterRamo || "ALL"} onValueChange={(v) => setFilterRamo(v === "ALL" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  {ramosDisponibles.map((ramo) => (
                    <SelectItem key={ramo} value={ramo}>{ramo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-36">
              <Label className="text-xs text-slate-500">Fecha Desde</Label>
              <Input
                type="date"
                value={filterFechaDesde}
                onChange={(e) => setFilterFechaDesde(e.target.value)}
              />
            </div>
            <div className="w-36">
              <Label className="text-xs text-slate-500">Fecha Hasta</Label>
              <Input
                type="date"
                value={filterFechaHasta}
                onChange={(e) => setFilterFechaHasta(e.target.value)}
              />
            </div>
            <Button variant="outline" onClick={clearFilters}>
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Banner de filtro activo por ejecutivo */}
      {filterEjecutivo && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-4 py-2" data-testid="filtro-ejecutivo-activo">
          <div className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-200">
            <User className="w-4 h-4" />
            Mostrando checklists de: <strong>{filterEjecutivo}</strong>
            <Badge variant="secondary">{total}</Badge>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilterEjecutivo("")}
            data-testid="quitar-filtro-ejecutivo-btn"
            className="text-blue-700 dark:text-blue-300"
          >
            <XCircle className="w-4 h-4 mr-1" /> Quitar filtro
          </Button>
        </div>
      )}

      {/* Results Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-auto max-h-[65vh]">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-800 border-y sticky top-0 z-20 [&_th]:bg-slate-50 dark:[&_th]:bg-slate-800 shadow-sm">
                <tr>
                  <th className="p-3 text-left text-xs font-medium text-slate-500 uppercase">Cliente</th>
                  <th className="p-3 text-left text-xs font-medium text-slate-500 uppercase">Póliza</th>
                  <th className="p-3 text-left text-xs font-medium text-slate-500 uppercase">Ramo</th>
                  <th className="p-3 text-left text-xs font-medium text-slate-500 uppercase">Compañía</th>
                  <th className="p-3 text-center text-xs font-medium text-slate-500 uppercase">Cobertura</th>
                  <th className="p-3 text-center text-xs font-medium text-slate-500 uppercase">Versión</th>
                  <th className="p-3 text-left text-xs font-medium text-slate-500 uppercase">Fecha</th>
                  <th className="p-3 text-left text-xs font-medium text-slate-500 uppercase">Realizado Por</th>
                  {isAdminOrSupervisor && (
                    <th className="p-3 text-left text-xs font-medium text-slate-500 uppercase" data-testid="col-tiempo-realizacion-header">
                      Tiempo realización
                    </th>
                  )}
                  <th className="p-3 text-left text-xs font-medium text-slate-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {checklists.length === 0 ? (
                  <tr>
                    <td colSpan={isAdminOrSupervisor ? 10 : 9} className="p-8 text-center text-slate-500">
                      No hay checklists completados
                    </td>
                  </tr>
                ) : (
                  checklists.map((checklist) => (
                    <tr key={checklist.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="p-3 text-sm font-medium">
                        <div className="flex items-center gap-2">
                          {checklist.cliente}
                          {checklist.revisado && (
                            <Badge
                              className="bg-emerald-100 text-emerald-700 border border-emerald-300 gap-1 text-xs"
                              title={`Revisado por ${checklist.revisado_por || ""}${checklist.revisado_at ? " · " + new Date(checklist.revisado_at).toLocaleString("es-DO") : ""}`}
                              data-testid={`revisado-badge-${checklist.id}`}
                            >
                              <ShieldCheck className="w-3 h-3" /> Revisado
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-sm">{checklist.poliza}</td>
                      <td className="p-3">
                        <Badge variant="outline" className="bg-blue-50 text-blue-700">
                          {checklist.ramo}
                        </Badge>
                      </td>
                      <td className="p-3 text-sm">{checklist.compania || "-"}</td>
                      <td className="p-3 text-center">
                        <Badge className={getColorByPercentage(checklist.porcentaje_cobertura)}>
                          {checklist.porcentaje_cobertura}%
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="secondary" className="font-mono text-xs">
                          V {checklist.plantilla_version || "1.0"}
                        </Badge>
                      </td>
                      <td className="p-3 text-sm">{checklist.fecha}</td>
                      <td className="p-3 text-sm">{checklist.realizado_por}</td>
                      {isAdminOrSupervisor && (
                        <td className="p-3 text-sm font-mono text-slate-600 dark:text-slate-300" data-testid={`tiempo-realizacion-${checklist.id}`}>
                          {checklist.tiempo_realizacion || "—"}
                        </td>
                      )}
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => handleViewChecklist(checklist)} title="Ver detalles">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleEditChecklist(checklist)} title="Editar checklist" className="text-blue-600">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleExportExcel(checklist.id)} title="Exportar Excel">
                            <FileSpreadsheet className="w-4 h-4 text-green-600" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleExportPDF(checklist.id)} title="Exportar PDF" data-testid={`export-pdf-${checklist.id}`}>
                            <FileText className="w-4 h-4 text-red-600" />
                          </Button>
                          {isAdminOrSupervisor && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleExportTrazabilidadPDF(checklist.id)}
                              title="Imprimir trazabilidad (aval de auditoría)"
                              className="text-amber-600"
                              data-testid={`print-trazabilidad-${checklist.id}`}
                            >
                              <History className="w-4 h-4" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDeleteChecklist(checklist)} title="Eliminar">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* View Checklist Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle del Checklist</DialogTitle>
            <DialogDescription>
              {selectedChecklist?.cliente} - {selectedChecklist?.ramo}
            </DialogDescription>
          </DialogHeader>
          
          {selectedChecklist && (
            <Tabs defaultValue="detalle" className="py-2">
              <TabsList>
                <TabsTrigger value="detalle" data-testid="checklist-tab-detalle">Detalle</TabsTrigger>
                {selectedChecklist.puede_ver_trazabilidad && (
                  <TabsTrigger value="trazabilidad" data-testid="checklist-tab-trazabilidad">
                    <History className="w-4 h-4 mr-1.5" /> Trazabilidad
                  </TabsTrigger>
                )}
              </TabsList>
              <TabsContent value="detalle" className="space-y-6 py-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded">
                  <Label className="text-xs text-slate-500">Cliente</Label>
                  <p className="font-medium">{selectedChecklist.cliente}</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded">
                  <Label className="text-xs text-slate-500">Póliza</Label>
                  <p className="font-medium">{selectedChecklist.poliza}</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded">
                  <Label className="text-xs text-slate-500">Compañía</Label>
                  <p className="font-medium">{selectedChecklist.compania || "-"}</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded">
                  <Label className="text-xs text-slate-500">Ramo</Label>
                  <p className="font-medium">{selectedChecklist.ramo}</p>
                  
                  {/* Tipo de Cobertura - Editable */}
                  {editingTipoCobertura ? (
                    <div className="mt-2 flex gap-2 items-center">
                      <Select value={newTipoCobertura} onValueChange={setNewTipoCobertura}>
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Daños Propios">Daños Propios</SelectItem>
                          <SelectItem value="Daños a Tercero">Daños a Tercero</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="ghost" className="h-8 px-2" onClick={handleSaveTipoCobertura}>
                        <Save className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditingTipoCobertura(false)}>
                        <XCircle className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-1 flex gap-2 items-center">
                      {selectedChecklist.tipo_cobertura_seleccionado ? (
                        <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                          {selectedChecklist.tipo_cobertura_seleccionado}
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-400">Sin tipo</span>
                      )}
                      {/* Show edit button for Vehiculo de Motor */}
                      {selectedChecklist.ramo?.toLowerCase().includes("vehiculo") && (
                        <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => {
                          setNewTipoCobertura(selectedChecklist.tipo_cobertura_seleccionado || "");
                          setEditingTipoCobertura(true);
                        }}>
                          <Edit className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded">
                  <Label className="text-xs text-slate-500">Fecha</Label>
                  <p className="font-medium">{selectedChecklist.fecha}</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded">
                  <Label className="text-xs text-slate-500">Realizado Por</Label>
                  <p className="font-medium">{selectedChecklist.realizado_por}</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded">
                  <Label className="text-xs text-slate-500">Supervisado Por</Label>
                  <p className="font-medium">{selectedChecklist.supervisado_por || "-"}</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded">
                  <Label className="text-xs text-slate-500">% Cobertura</Label>
                  <Badge className={`text-lg ${getColorByPercentage(selectedChecklist.porcentaje_cobertura)}`}>
                    {selectedChecklist.porcentaje_cobertura}%
                  </Badge>
                </div>
              </div>

              {/* Revisión por Supervisor/Admin */}
              {isAdminOrSupervisor && (
                <div className="flex flex-wrap items-center gap-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 px-4 py-3 rounded" data-testid="ver-revisado-bar">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <Checkbox
                      checked={!!selectedChecklist.revisado}
                      disabled={togglingRevisado}
                      onCheckedChange={(v) => handleToggleRevisado(selectedChecklist.id, !!v)}
                      data-testid="ver-revisado-checkbox"
                    />
                    <span className="text-sm font-medium text-emerald-800 dark:text-emerald-200 flex items-center gap-1">
                      <ShieldCheck className="w-4 h-4" /> Revisado
                    </span>
                  </label>
                  {selectedChecklist.revisado && (
                    <span className="text-xs text-emerald-700 dark:text-emerald-300">
                      Por <strong>{selectedChecklist.revisado_por || "-"}</strong>
                      {selectedChecklist.revisado_at && <> · {new Date(selectedChecklist.revisado_at).toLocaleString("es-DO")}</>}
                    </span>
                  )}
                </div>
              )}

              {/* Ticket de Mesa de Ayuda asociado (identificación rápida) */}
              {selectedChecklist.ticket_numero && (
                <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-2.5 rounded" data-testid="checklist-ticket-ref">
                  <Ticket className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-slate-700 dark:text-slate-200">Ticket Mesa de Ayuda:</span>
                  <Badge className="bg-amber-600 text-white">Ticket #{selectedChecklist.ticket_numero}</Badge>
                </div>
              )}

              {/* Items Table */}
              <div>
                <h3 className="font-semibold mb-3">Items de Evaluación</h3>
                {(() => {
                  const items = selectedChecklist.items || [];
                  const hasComp = items.some((i) => i.cotizacion_estado);
                  const hasMontos = !hasComp && items.some((i) => (i.monto_asegurado || "").toString().trim() || (i.ajuste_solicitado || "").toString().trim());
                  const rgEstado = (i) => (i.no_aplica ? "no_aplica" : i.tiene_cobertura ? "si" : "no");
                  const isDif = (i) => i.cotizacion_estado && i.cotizacion_estado !== rgEstado(i);
                  const nDif = hasComp ? items.filter(isDif).length : 0;
                  const estadoBadge = (e) =>
                    e === "no_aplica" ? <Badge className="bg-slate-500 text-white">N/A</Badge>
                    : e === "si" ? <Badge className="bg-green-500 text-white">SI</Badge>
                    : e === "no" ? <Badge className="bg-red-500 text-white">NO</Badge>
                    : <span className="text-slate-400">—</span>;
                  return (
                    <>
                      {hasComp && (
                        <div
                          className={`mb-2 px-3 py-2 rounded border text-sm font-semibold ${nDif > 0 ? "bg-red-50 border-red-300 text-red-700" : "bg-emerald-50 border-emerald-300 text-emerald-700"}`}
                          data-testid="detalle-comparacion-banner"
                        >
                          {nDif > 0 ? `HAY DIFERENCIAS: ${nDif} cobertura(s) difieren del checklist de cotizaciones` : "Sin diferencias con el checklist de cotizaciones"}
                        </div>
                      )}
                      <div className="border rounded overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100 dark:bg-slate-800">
                            <tr>
                              <th className="p-2 text-left w-8">#</th>
                              <th className="p-2 text-left">Cobertura/Aspecto</th>
                              {hasComp && <th className="p-2 text-center w-16">Cotiz.</th>}
                              <th className="p-2 text-center w-20">Tiene</th>
                              {hasComp && <th className="p-2 text-left">Diferencia</th>}
                              {hasMontos && <th className="p-2 text-left w-28">Monto Aseg.</th>}
                              {hasMontos && <th className="p-2 text-left">Ajuste Solicitado</th>}
                              <th className="p-2 text-left">Com. Ejecutivo</th>
                              <th className="p-2 text-left">Com. Supervisor</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, idx) => (
                              <tr key={item.item_id} className={idx % 2 === 0 ? "bg-white dark:bg-slate-900" : "bg-slate-50 dark:bg-slate-800"}>
                                <td className="p-2 text-slate-500">{idx + 1}</td>
                                <td className="p-2">{item.texto}</td>
                                {hasComp && <td className="p-2 text-center" data-testid={`detalle-cotiz-${item.item_id}`}>{estadoBadge(item.cotizacion_estado)}</td>}
                                <td className="p-2 text-center">{estadoBadge(rgEstado(item))}</td>
                                {hasComp && (
                                  <td className="p-2" data-testid={`detalle-dif-${item.item_id}`}>
                                    {isDif(item) && <Badge className="bg-red-100 text-red-700 border border-red-300 mr-1">HAY DIFERENCIAS</Badge>}
                                    <span className="text-slate-600">{item.diferencia || (isDif(item) ? "" : "-")}</span>
                                  </td>
                                )}
                                {hasMontos && <td className="p-2 text-slate-600" data-testid={`detalle-monto-${item.item_id}`}>{item.monto_asegurado || "-"}</td>}
                                {hasMontos && <td className="p-2 text-slate-600" data-testid={`detalle-ajuste-${item.item_id}`}>{item.ajuste_solicitado || "-"}</td>}
                                <td className="p-2 text-slate-600">{item.comentario_ejecutivo || "-"}</td>
                                <td className="p-2 text-slate-600">{item.comentario_supervisor || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Stats */}
              <div className="flex gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span>{selectedChecklist.items_con_cobertura} con cobertura</span>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-600" />
                  <span>{selectedChecklist.total_items - selectedChecklist.items_con_cobertura} sin cobertura</span>
                </div>
                {selectedChecklist.items_no_aplica > 0 && (
                  <div className="flex items-center gap-2">
                    <MinusCircle className="w-4 h-4 text-slate-400" />
                    <span>{selectedChecklist.items_no_aplica} N/A</span>
                  </div>
                )}
              </div>

              {/* Observations */}
              {selectedChecklist.observaciones_generales && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded">
                  <Label className="text-xs text-slate-500">Observaciones Generales</Label>
                  <p className="mt-1">{selectedChecklist.observaciones_generales}</p>
                </div>
              )}
              </TabsContent>

              {selectedChecklist.puede_ver_trazabilidad && (
                <TabsContent value="trazabilidad" className="py-4">
                  <ChecklistTrazabilidad checklist={selectedChecklist} />
                </TabsContent>
              )}
            </Tabs>
          )}

          <DialogFooter>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleExportExcel(selectedChecklist?.id)}>
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                Excel
              </Button>
              <Button variant="outline" onClick={() => handleExportPDF(selectedChecklist?.id)}>
                <FileText className="w-4 h-4 mr-2 text-red-600" />
                PDF
              </Button>
              <Button onClick={() => setShowViewDialog(false)}>Cerrar</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Checklist Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(v) => !v && closeEditDialog()}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Editar Checklist
            </DialogTitle>
            <DialogDescription>
              {editingChecklist?.cliente} - {editingChecklist?.ramo}
            </DialogDescription>
          </DialogHeader>

          {editingChecklist && (
            <div className="space-y-6 py-4">
              {/* Client Info Header */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <Label className="text-xs text-slate-500">Cliente</Label>
                  <p className="font-medium text-sm">{editingChecklist.cliente}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Póliza</Label>
                  <p className="font-medium text-sm">{editingChecklist.poliza}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Ramo</Label>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">
                    {editingChecklist.ramo}
                  </Badge>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Compañía</Label>
                  <p className="font-medium text-sm">{editingChecklist.compania || "-"}</p>
                </div>
              </div>

              {/* Evaluator Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    Realizado Por
                  </Label>
                  <Input
                    value={editingChecklist.realizado_por || ""}
                    disabled
                    className="bg-slate-100"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    Supervisado Por
                  </Label>
                  <Input
                    value={editSupervisadoPor}
                    onChange={(e) => setEditSupervisadoPor(e.target.value)}
                    placeholder="Nombre del supervisor..."
                  />
                </div>
              </div>

              {/* Revisión por Supervisor/Admin (sincronizado con la vista Ver) */}
              {isAdminOrSupervisor && (
                <div className="flex flex-wrap items-center gap-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 px-4 py-3 rounded" data-testid="edit-revisado-bar">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <Checkbox
                      checked={!!editingChecklist.revisado}
                      disabled={togglingRevisado}
                      onCheckedChange={(v) => handleToggleRevisado(editingChecklist.id, !!v)}
                      data-testid="edit-revisado-checkbox"
                    />
                    <span className="text-sm font-medium text-emerald-800 dark:text-emerald-200 flex items-center gap-1">
                      <ShieldCheck className="w-4 h-4" /> Revisado
                    </span>
                  </label>
                  {editingChecklist.revisado && (
                    <span className="text-xs text-emerald-700 dark:text-emerald-300">
                      Por <strong>{editingChecklist.revisado_por || "-"}</strong>
                      {editingChecklist.revisado_at && <> · {new Date(editingChecklist.revisado_at).toLocaleString("es-DO")}</>}
                    </span>
                  )}
                </div>
              )}

              {/* Progress Bar */}
              <div className="p-4 bg-white dark:bg-slate-900 border rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium">Progreso de Cobertura</span>
                  <span className={`text-lg font-bold ${
                    editPorcentaje >= 80 ? 'text-green-600' : 
                    editPorcentaje >= 50 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {editPorcentaje}%
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full transition-all duration-300 ${
                      editPorcentaje >= 80 ? 'bg-green-500' : 
                      editPorcentaje >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${editPorcentaje}%` }}
                  />
                </div>
                <div className="flex gap-4 mt-2 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-green-500" />
                    {editItemsConCobertura} con cobertura
                  </span>
                  <span className="flex items-center gap-1">
                    <XCircle className="w-3 h-3 text-red-500" />
                    {editTotalItems - editItemsConCobertura} sin cobertura
                  </span>
                  {editItemsNoAplica > 0 && (
                    <span className="flex items-center gap-1">
                      <MinusCircle className="w-3 h-3 text-slate-400" />
                      {editItemsNoAplica} N/A
                    </span>
                  )}
                </div>
              </div>

              {/* Checklist Items by Category */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 dark:text-white">
                  Items de Evaluación
                </h3>
                
                {Object.entries(groupedEditItems).map(([categoria, items]) => (
                  <div key={categoria} className="border rounded-lg overflow-hidden">
                    <div className="bg-slate-100 dark:bg-slate-800 px-4 py-2 font-medium text-sm">
                      {categoria}
                    </div>
                    <div className="divide-y">
                      {items.map((item, idx) => (
                        <div 
                          key={item.item_id} 
                          className={`p-3 ${
                            item.estado === "si" ? 'bg-green-50 dark:bg-green-900/20' : 
                            item.estado === "no_aplica" ? 'bg-slate-100 dark:bg-slate-800/50' :
                            'bg-white dark:bg-slate-900'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Estado buttons - SI / NO / N/A */}
                            <div className="flex gap-1 mt-1">
                              <button
                                onClick={() => handleEditItemEstadoChange(item.item_id, item.estado === "si" ? "pendiente" : "si")}
                                className={`w-10 h-8 rounded flex items-center justify-center text-xs font-bold transition-colors ${
                                  item.estado === "si" 
                                    ? 'bg-green-500 text-white' 
                                    : 'bg-slate-200 text-slate-600 hover:bg-green-100'
                                }`}
                              >
                                SI
                              </button>
                              <button
                                onClick={() => handleEditItemEstadoChange(item.item_id, item.estado === "no_aplica" ? "pendiente" : "no_aplica")}
                                className={`w-10 h-8 rounded flex items-center justify-center text-xs font-bold transition-colors ${
                                  item.estado === "no_aplica" 
                                    ? 'bg-slate-500 text-white' 
                                    : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                                }`}
                                title="No Aplica - no se contabiliza en el progreso"
                              >
                                N/A
                              </button>
                            </div>
                            
                            {/* Item content */}
                            <div className="flex-1">
                              <p className={`font-medium text-sm ${
                                item.estado === "no_aplica" 
                                  ? 'text-slate-400 line-through' 
                                  : 'text-slate-900 dark:text-white'
                              }`}>
                                {idx + 1}. {item.texto}
                              </p>
                              
                              {/* Comments - only show if not N/A */}
                              {item.estado !== "no_aplica" && (
                              <div className="grid grid-cols-2 gap-2 mt-2">
                                <div>
                                  <Label className="text-xs text-slate-500">Comentario Ejecutivo</Label>
                                  <Input
                                    value={item.comentario_ejecutivo || ""}
                                    onChange={(e) => handleEditComentarioEjecutivo(item.item_id, e.target.value)}
                                    placeholder="Agregar comentario..."
                                    className="text-sm h-8"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-slate-500">Comentario Supervisor</Label>
                                  <Input
                                    value={item.comentario_supervisor || ""}
                                    onChange={(e) => handleEditComentarioSupervisor(item.item_id, e.target.value)}
                                    placeholder="Agregar comentario..."
                                    className="text-sm h-8"
                                  />
                                </div>
                              </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Observations */}
              <div>
                <Label>Observaciones Generales</Label>
                <Textarea
                  value={editObservaciones}
                  onChange={(e) => setEditObservaciones(e.target.value)}
                  placeholder="Observaciones adicionales sobre la evaluación..."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeEditDialog} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Guardar Cambios
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog for bulk updating tipo cobertura */}
      <Dialog open={showBulkUpdateDialog} onOpenChange={setShowBulkUpdateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Actualizar Tipo de Cobertura</DialogTitle>
            <DialogDescription>
              Actualizar el tipo de cobertura para checklists existentes que no lo tienen configurado.
            </DialogDescription>
          </DialogHeader>

          {checklistsSinTipo.length === 0 ? (
            <div className="py-8 text-center text-slate-500">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500" />
              <p className="text-lg font-medium">¡Todos los checklists están actualizados!</p>
              <p className="text-sm">No hay checklists que requieran tipo de cobertura.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Se encontraron <strong>{checklistsSinTipo.length}</strong> checklists sin tipo de cobertura asignado.
                </p>
              </div>

              <div className="max-h-[300px] overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Cliente</th>
                      <th className="text-left p-2">Póliza</th>
                      <th className="text-left p-2">Ramo</th>
                      <th className="text-left p-2">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checklistsSinTipo.map((checklist) => (
                      <tr key={checklist.id} className="border-t hover:bg-slate-50 dark:hover:bg-slate-800">
                        <td className="p-2">{checklist.cliente}</td>
                        <td className="p-2">{checklist.poliza}</td>
                        <td className="p-2">
                          <Badge variant="outline">{checklist.ramo}</Badge>
                        </td>
                        <td className="p-2">{checklist.fecha}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="space-y-2">
                <Label>Seleccionar Tipo de Cobertura</Label>
                <Select value={bulkTipoCobertura} onValueChange={setBulkTipoCobertura}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tipo..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Daños Propios">Daños Propios</SelectItem>
                    <SelectItem value="Daños a Tercero">Daños a Tercero</SelectItem>
                    <SelectItem value="Formato Americano">Formato Americano</SelectItem>
                    <SelectItem value="Formato Inglés">Formato Inglés</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Este tipo de cobertura se aplicará a todos los checklists listados arriba.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkUpdateDialog(false)}>
              Cerrar
            </Button>
            {checklistsSinTipo.length > 0 && (
              <Button onClick={handleBulkUpdateTipoCobertura} disabled={loadingBulk || !bulkTipoCobertura}>
                {loadingBulk ? "Actualizando..." : `Actualizar ${checklistsSinTipo.length} Checklists`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============== TRAZABILIDAD (AUDITORÍA) ==============
const ACCION_META = {
  inicio: { label: "Inicio", color: "bg-blue-100 text-blue-700", Icon: Clock },
  documento_ia: { label: "Documento IA", color: "bg-purple-100 text-purple-700", Icon: Sparkles },
  guardado: { label: "Guardado", color: "bg-green-100 text-green-700", Icon: Save },
  pregunta_ticket: { label: "Pregunta ticket", color: "bg-amber-100 text-amber-700", Icon: MessageCircleQuestion },
  ticket_aceptado: { label: "Aceptó", color: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  ticket_rechazado: { label: "Rechazó", color: "bg-slate-100 text-slate-600", Icon: XCircle },
  ticket_creado: { label: "Ticket creado", color: "bg-amber-100 text-amber-800", Icon: Ticket },
  revisado: { label: "Revisado", color: "bg-emerald-100 text-emerald-700", Icon: ShieldCheck },
  revision_quitada: { label: "Revisión quitada", color: "bg-slate-100 text-slate-600", Icon: XCircle },
};

const fmtEventoFecha = (ts) => {
  if (!ts) return { fecha: "-", hora: "-" };
  try {
    const d = new Date(ts);
    return {
      fecha: d.toLocaleDateString("es-DO", { year: "numeric", month: "2-digit", day: "2-digit" }),
      hora: d.toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
    };
  } catch {
    return { fecha: ts, hora: "-" };
  }
};

function ChecklistTrazabilidad({ checklist }) {
  const eventos = [...(checklist.trazabilidad || [])].sort(
    (a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0)
  );

  return (
    <div data-testid="checklist-trazabilidad">
      <div className="flex items-center gap-2 mb-1">
        <History className="w-5 h-5 text-[#193966]" />
        <h3 className="font-semibold text-[#193966]">Trazabilidad del Checklist</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Registro de auditoría: cada acción ejecutada desde que se inició el checklist hasta su cierre.
        {checklist.ticket_numero && (
          <> Ticket asociado: <strong>#{checklist.ticket_numero}</strong>.</>
        )}
      </p>

      {eventos.length === 0 ? (
        <p className="text-slate-400 italic py-6 text-center">
          No hay eventos de trazabilidad registrados para este checklist.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#193966] text-white">
                <th className="text-left px-4 py-2.5 font-semibold w-28">Fecha</th>
                <th className="text-left px-4 py-2.5 font-semibold w-24">Hora</th>
                <th className="text-left px-4 py-2.5 font-semibold w-40">Usuario</th>
                <th className="text-left px-4 py-2.5 font-semibold w-40">Acción</th>
                <th className="text-left px-4 py-2.5 font-semibold">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {eventos.map((e, i) => {
                const meta = ACCION_META[e.accion] || { label: e.accion, color: "bg-slate-100 text-slate-600", Icon: FileText };
                const { fecha, hora } = fmtEventoFecha(e.timestamp);
                const Icon = meta.Icon;
                return (
                  <tr key={i} className={i % 2 === 0 ? "bg-white dark:bg-slate-900" : "bg-slate-50 dark:bg-slate-800"} data-testid={`trazabilidad-row-${i}`}>
                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300 whitespace-nowrap">{fecha}</td>
                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300 whitespace-nowrap font-mono text-xs">{hora}</td>
                    <td className="px-4 py-2.5 text-slate-700 dark:text-slate-200">{e.usuario || "-"}</td>
                    <td className="px-4 py-2.5">
                      <Badge className={`${meta.color} border-0 gap-1`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{e.detalle || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
